import { Timestamp } from 'firebase/firestore'
import { Dispatch, SetStateAction } from 'react'
import { Address } from './AddressInterface'
import { User } from './UserInterface'
import { Cause } from './CauseInterface'

export interface OrgTheme {
  primaryColor: string
  logo: string
  secondaryColor: string
  backgroundColor?: string
}

export interface CustomDonationAmount {
  default: boolean
  amount: string
}

export interface Org {
  id?: string
  address?: Address
  cot?: boolean
  distance?: number
  totalDonations?: number
  name?: string
  about?: string
  addressMailing?: string
  adminEmail?: string
  causes?: string[]
  coverPhoto?: string
  createdAt?: Timestamp
  customDonationAmounts?: CustomDonationAmount[]
  customUrl?: string
  distanceInM?: number
  ein?: string
  events?: number
  facebook?: string
  followers?: number
  hash?: string
  img?: string
  instagram?: string
  linkedin?: string
  latLng?: { lng: number; lat: number }
  lowercaseName?: string
  members?: string[]
  opportunities?: number
  owner?: string
  onboarded?: boolean
  pendingVerification?: boolean
  phone?: string
  profilePic?: string
  stripeComplete?: boolean
  stripeID?: string
  theme?: OrgTheme
  totalDonationsCount?: number
  totalFees?: number
  totalNetDonations?: number
  trendingScore?: number
  twitter?: string
  type?: string
  useMailingAddress?: boolean
  userName?: string
  verified?: boolean
  website?: string
  totalOneTimeDonations?: number
  stripeCustomerId?: string
  totalMatchAmount?: number
  employees?: number
  matchPercentage?: number
  maxEmployeeMatchPerYear?: number
  maxCompanyMatchPerYear?: number
  matchAnyDonation?: boolean
  matchSettingsComplete?: boolean
  allowDonationsToAllNonprofits?: boolean
  supportedNonprofits?: string[]
  allowAnonymousDonations?: boolean
  autoApproveDonations?: boolean
  fiscalYear?: string
  hourlyVolunteerCompensation?: number
  stripeSettingsComplete?: boolean
  trial?: boolean
  confirmEmail?: string
  useWalletAsDefault?: boolean
}

export interface Nonprofit extends Org {
  raised?: number
  status?: string
  permit?: string
  cause?: string
  donated?: number
  totalVolunteerHours?: number
}

export interface Member {
  id: string
  active?: boolean
  email?: string
  hideTips?: boolean
  postNewOpportunities?: boolean
  position?: string
  publicPosts?: boolean
  role?: string
  verified?: boolean
  viewDonations?: boolean
  volunteerApplicationReview?: boolean
  volunteerCheckIn?: boolean
  invite?: boolean
  receiveNewApplications?: boolean
  receivePostComments?: boolean
  firstName?: string
  lastName?: string
  jobTitle?: string
  stripeCustomerId?: string
}

export interface MemberSubCollection {
  id?: string
  active: boolean
  postNewOpportunities: boolean
  publicPosts: boolean
  role: string
  verified: boolean
  viewDonations: boolean
  volunteerApplicationReview: boolean
  volunteerCheckIn: boolean
}

export interface CustomLinks {
  id?: string
  createdAt: Timestamp
  creator: string
  customUrl: string
  lowerCaseName: string
  name: string
  status: string
  backers?: string[]
  totalDonations?: number
}

export interface ExtraInfo {
  id?: string
  sendStripePending: boolean
  sentStripeEnabled: boolean
}

export interface InviteRequest {
  id: string
  orgId: string
  invite: boolean
  [key: string]: unknown
}

export type OrgMemberProfile = User & { role?: string; verified?: boolean } & Partial<Member>

export interface OrgMembersData {
  members: OrgMemberProfile[]
  sponsors: OrgMemberProfile[]
  userType?: string
  currentMember?: OrgMemberProfile
}

export interface Invite {
  id: string
  expires: Timestamp | Date | string | number
  orgId: string
  org?: Org
  passcodes?: Array<{ passcode: number }>
}

export interface CreateCustomLinkParams {
  orgId: string
  name: string
  customUrl: string
}

export interface UpdateCustomLinkParams {
  orgId: string
  id: string
  data: {
    name?: string
    customUrl?: string
  }
}

export interface GetCustomLinksByOrgIdParams {
  orgId: string
  links: CustomLinks[]
  currentPage: number
  nextPage: number
  linksCount: number
  rowsPerPage: number
  search?: string
  startDate?: Date
  endDate?: Date
}

// Context Props
export interface OrganizationContextProps {
  ourOrgs: Org[]
  setOurOrgs: Dispatch<SetStateAction<Org[]>>
  ourOrgOptions: Array<{ value: string; label: string; org: Org }>
  setOurOrgOptions: Dispatch<SetStateAction<Array<{ value: string; label: string; org: Org }>>>
  selectedOrg: Org | null
  setSelectedOrg: Dispatch<SetStateAction<Org | null>>
  members: Member[]
  setMembers: Dispatch<SetStateAction<Member[]>>
  users: Member[] | null
  setUsers: Dispatch<SetStateAction<Member[] | null>>
  invitedMembers: Member[]
  currentMember: Member | null
  causes: Cause[]
  setCauses: Dispatch<SetStateAction<Cause[]>>
  coverPhoto: string | null
  setCoverPhoto: Dispatch<SetStateAction<string | null>>
  owner: boolean
  userType: string
  sponsors: Member[]
  loading: boolean
  newOrg: Org | null
  setNewOrg: Dispatch<SetStateAction<Org | null>>
  newOrgCoverPhotos: string[]
  setNewOrgCoverPhotos: Dispatch<SetStateAction<string[]>>
  feed: unknown[]
  refreshing: boolean
  onRefresh: (showLoader?: boolean) => void
  loadMoreFeed: () => void
  stripeConnectAccount: Record<string, unknown> | null
  pendingChanges: boolean
  setPendingChanges: Dispatch<SetStateAction<boolean>>
  exitModal: boolean
  setExitModal: Dispatch<SetStateAction<boolean>>
}

export const OrganizationContextDefaultValues: OrganizationContextProps = {
  ourOrgs: [],
  setOurOrgs: () => {},
  ourOrgOptions: [],
  setOurOrgOptions: () => {},
  selectedOrg: null,
  setSelectedOrg: () => {},
  members: [],
  setMembers: () => {},
  users: null,
  setUsers: () => {},
  invitedMembers: [],
  currentMember: null,
  causes: [],
  setCauses: () => {},
  coverPhoto: null,
  setCoverPhoto: () => {},
  owner: false,
  userType: '',
  sponsors: [],
  loading: true,
  newOrg: null,
  setNewOrg: () => {},
  newOrgCoverPhotos: [],
  setNewOrgCoverPhotos: () => {},
  feed: [],
  refreshing: true,
  onRefresh: () => {},
  loadMoreFeed: () => {},
  stripeConnectAccount: null,
  pendingChanges: false,
  setPendingChanges: () => {},
  exitModal: false,
  setExitModal: () => {},
}
